import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Activity, TrendingUp, Clock } from 'lucide-react';
import { adminApi, type AdminWorld, type PaginatedResponse } from '../../lib/api';
import { StatCard } from '../../components/admin/StatCard';
import { LoadingSkeleton } from '../../components/admin/LoadingSkeleton';
import { EmptyState } from '../../components/admin/EmptyState';

export function WorldsPage() {
  const [worlds, setWorlds] = useState<PaginatedResponse<AdminWorld> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [timezoneFilter, setTimezoneFilter] = useState<string>('');
  const [lightbox, setLightbox] = useState<{ url: string; prompt?: string | null } | null>(
    null,
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await adminApi.getWorlds({
          page,
          search: search || undefined,
        });
        setWorlds(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load worlds');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [page, search]);

  // Compute stats from loaded data
  const stats = useMemo(() => {
    if (!worlds) return null;

    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const activeCount = worlds.data.filter(
      (world) => world.lastInboundAt && new Date(world.lastInboundAt) > sevenDaysAgo,
    ).length;

    const totalLettersIn = worlds.data.reduce((sum, world) => sum + world.counts.lettersIn, 0);
    const totalLettersOut = worlds.data.reduce((sum, world) => sum + world.counts.lettersOut, 0);

    const avgInOut =
      worlds.data.length > 0
        ? ((totalLettersIn + totalLettersOut) / worlds.data.length).toFixed(1)
        : '0';

    return {
      total: worlds.pagination.total,
      active: activeCount,
      lettersIn: totalLettersIn,
      lettersOut: totalLettersOut,
      avgInOut,
    };
  }, [worlds]);

  // Apply client-side filters
  const filteredWorlds = useMemo(() => {
    if (!worlds) return [];

    return worlds.data.filter((world) => {
      if (userFilter && !world.user.email.toLowerCase().includes(userFilter.toLowerCase())) {
        return false;
      }
      if (timezoneFilter && world.timezone !== timezoneFilter) return false;
      return true;
    });
  }, [worlds, userFilter, timezoneFilter]);

  // Extract unique timezones for filter
  const availableTimezones = useMemo(() => {
    if (!worlds) return [];
    const timezones = new Set(worlds.data.map((w) => w.timezone));
    return Array.from(timezones).sort();
  }, [worlds]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPage(1);
  };

  const handleClearFilters = () => {
    setUserFilter('');
    setTimezoneFilter('');
    setSearch('');
    setPage(1);
  };

  const hasActiveFilters = userFilter || timezoneFilter || search;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-xl sm:text-2xl text-parchment">Worlds</h1>
          <p className="text-mist text-sm mt-1">Manage user worlds and activity</p>
        </div>
      </div>

      {/* Summary Statistics */}
      {isLoading ? (
        <LoadingSkeleton variant="grid" />
      ) : stats ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Worlds" value={stats.total} icon={Globe} />
          <StatCard
            label="Active (7d)"
            value={stats.active}
            subtitle={`${((stats.active / stats.total) * 100).toFixed(0)}% of total`}
            icon={Activity}
            variant="success"
          />
          <StatCard
            label="Letters In/Out"
            value={`${stats.lettersIn} / ${stats.lettersOut}`}
            subtitle="Total correspondence"
            icon={TrendingUp}
            variant="info"
          />
          <StatCard label="Avg Letters/World" value={stats.avgInOut} icon={Clock} />
        </div>
      ) : null}

      {/* Filters */}
      <div className="bg-night border border-stone/30 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search by Address */}
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by address or token..."
              className="flex-1 px-4 py-2 bg-void border border-stone/30 rounded-lg text-parchment placeholder-mist/50 text-sm focus:outline-none focus:border-rune"
            />
            <button
              type="submit"
              className="px-4 py-2 bg-rune/20 text-rune border border-rune/30 rounded-lg hover:bg-rune/30 transition-colors text-sm whitespace-nowrap"
            >
              Search
            </button>
          </form>

          {/* User Filter */}
          <input
            type="text"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            placeholder="Filter by user email..."
            className="px-4 py-2 bg-void border border-stone/30 rounded-lg text-parchment placeholder-mist/50 text-sm focus:outline-none focus:border-rune"
          />

          {/* Timezone Filter */}
          <select
            value={timezoneFilter}
            onChange={(e) => setTimezoneFilter(e.target.value)}
            className="px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
          >
            <option value="">All Timezones</option>
            {availableTimezones.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-stone/30 text-mist rounded-lg hover:bg-stone/50 transition-colors text-sm whitespace-nowrap"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-ember/10 border border-ember/30 rounded-lg text-ember">{error}</div>
      )}

      {/* Worlds Table */}
      {isLoading ? (
        <LoadingSkeleton variant="table" rows={10} cols={7} />
      ) : filteredWorlds.length === 0 ? (
        <EmptyState
          variant={hasActiveFilters ? 'no-results' : 'no-data'}
          title={hasActiveFilters ? 'No worlds match your filters' : 'No worlds found'}
          description={
            hasActiveFilters ? 'Try adjusting your search or filter criteria.' : undefined
          }
          action={
            hasActiveFilters ? { label: 'Clear Filters', onClick: handleClearFilters } : undefined
          }
        />
      ) : (
        <>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <div className="bg-night border border-stone/30 rounded-xl overflow-hidden">
                <table className="w-full min-w-[980px] text-sm">
                  <thead>
                    <tr className="text-mist text-left border-b border-stone/30 bg-stone/20">
                      <th className="px-4 py-3 font-medium">Thumb</th>
                      <th className="px-4 py-3 font-medium">Address</th>
                      <th className="px-4 py-3 font-medium">User</th>
                      <th className="px-4 py-3 font-medium text-center">Day</th>
                      <th className="px-4 py-3 font-medium text-center">Letters In/Out</th>
                      <th className="px-4 py-3 font-medium">Timezone</th>
                      <th className="px-4 py-3 font-medium">Last Activity</th>
                      <th className="px-4 py-3 font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredWorlds.map((world) => {
                      const totalLetters = world.counts.lettersIn + world.counts.lettersOut;
                      const inRatio =
                        totalLetters > 0 ? (world.counts.lettersIn / totalLetters) * 100 : 50;

                      return (
                        <tr
                          key={world.id}
                          className="border-b border-stone/20 last:border-0 hover:bg-stone/10 transition-colors"
                        >
                          <td className="px-4 py-3">
                            {world.thumbnailUrl ? (
                              <img
                                src={world.thumbnailUrl}
                                alt="World thumbnail"
                                className="h-10 w-10 rounded-md object-contain cursor-zoom-in"
                                loading="lazy"
                                onClick={() =>
                                  setLightbox({
                                    url: world.thumbnailUrl ?? '',
                                    prompt: world.thumbnailPrompt ?? null,
                                  })
                                }
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-md bg-stone/30 flex items-center justify-center text-[10px] text-mist">
                                —
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Link
                              to={`/admin/worlds/${world.id}`}
                              className="text-rune hover:text-rune-glow hover:underline font-mono text-xs"
                            >
                              {world.inboundAddress}
                            </Link>
                          </td>
                          <td className="px-4 py-3">
                            <Link
                              to={`/admin/users/${world.user.id}`}
                              className="text-mist hover:text-parchment hover:underline text-xs"
                            >
                              {world.user.email}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-center text-parchment">{world.dayIndex}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-parchment">
                                {world.counts.lettersIn} / {world.counts.lettersOut}
                              </span>
                              {/* Visual ratio bar */}
                              {totalLetters > 0 && (
                                <div className="w-16 h-1.5 bg-stone/30 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-rune/50"
                                    style={{ width: `${inRatio}%` }}
                                  ></div>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-mist text-xs">{world.timezone}</td>
                          <td className="px-4 py-3 text-mist text-xs">
                            {world.lastInboundAt
                              ? formatRelativeTime(world.lastInboundAt)
                              : 'Never'}
                          </td>
                          <td className="px-4 py-3 text-mist text-xs">
                            {formatDate(world.createdAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {worlds && worlds.pagination.totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-sm text-mist">
                Showing {filteredWorlds.length} of {worlds.pagination.total} worlds
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="px-3 py-2 bg-stone/30 text-mist rounded-lg text-sm focus:outline-none focus:border-rune"
                >
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-3 py-2.5 text-sm bg-stone/30 text-mist rounded-lg hover:bg-stone/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="px-3 py-2.5 text-sm text-mist">
                  Page {page} of {worlds.pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === worlds.pagination.totalPages}
                  className="px-3 py-2.5 text-sm bg-stone/30 text-mist rounded-lg hover:bg-stone/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-void/80 backdrop-blur-sm"
            onClick={() => setLightbox(null)}
          />
          <div className="relative max-h-[90vh] max-w-[90vw]">
            <button
              onClick={() => setLightbox(null)}
              className="absolute -top-4 -right-4 bg-void text-mist hover:text-parchment rounded-full border border-stone/40 w-8 h-8 flex items-center justify-center"
              aria-label="Close image"
            >
              ×
            </button>
            <div className="flex flex-col gap-3 max-h-[90vh] max-w-[90vw]">
              <img
                src={lightbox.url}
                alt="World thumbnail"
                className="max-h-[75vh] max-w-[90vw] rounded-lg object-contain bg-parchment/95"
              />
              {lightbox.prompt && (
                <div className="bg-void/95 border border-stone/40 rounded-lg p-3 text-xs text-mist max-h-40 overflow-auto">
                  {lightbox.prompt}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString();
}

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}
